import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Link2, Loader2, Sparkles, CheckCircle2, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_BASE = process.env.REACT_APP_SHARE_URL_BASE || process.env.REACT_APP_BACKEND_URL;

export default function StepSend({ config, kpiCurrent, buildHtml, goTo }) {
  const [subject, setSubject] = useState(`${config.month} ${config.year} Ecommerce Performance Report${config.country ? ` — ${config.country}` : ""}`);
  const [recipients, setRecipients] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState({ send: false, share: false, suggest: false });
  const [sentTo, setSentTo] = useState("");
  const [shareUrl, setShareUrl] = useState("");

  const suggestSubjects = async () => {
    setLoading((p) => ({ ...p, suggest: true }));
    try {
      const res = await axios.post(`${API}/generate-subject-lines`, {
        kpi_data: kpiCurrent || {},
        country: config.country || "Global",
        month: `${config.month} ${config.year}`,
        currency: config.currency,
      });
      if (res.data.success) setSuggestions(res.data.subjects);
      else toast.error("Couldn't generate suggestions");
    } catch { toast.error("Couldn't generate suggestions"); }
    finally { setLoading((p) => ({ ...p, suggest: false })); }
  };

  const sendEmail = async () => {
    const emails = recipients.split(",").map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Add at least one recipient email"); return; }
    if (emails.some((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))) { toast.error("One of the emails looks invalid"); return; }
    setLoading((p) => ({ ...p, send: true }));
    try {
      const res = await axios.post(`${API}/send-email`, {
        recipient_email: recipients,
        subject,
        html_content: buildHtml(),
      });
      if (res.data.success) {
        setSentTo(emails.join(", "));
        toast.success(res.data.message || "Email sent");
      } else toast.error(res.data.error || "Failed to send");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setLoading((p) => ({ ...p, send: false })); }
  };

  const saveAndShare = async () => {
    setLoading((p) => ({ ...p, share: true }));
    try {
      const res = await axios.post(`${API}/save-report`, {
        title: `${config.month} ${config.year} Report${config.country ? ` - ${config.country}` : ""} (v1)`,
        html_content: buildHtml(),
        config,
      });
      if (res.data.success) {
        const url = `${SHARE_BASE}/api/view-report/${res.data.report_id}`;
        setShareUrl(url);
        try { await navigator.clipboard.writeText(url); toast.success("Share link copied — passcode: ecom2026"); }
        catch { toast.success("Share link ready — passcode: ecom2026"); }
      } else toast.error("Failed to create share link");
    } catch { toast.error("Failed to create share link"); }
    finally { setLoading((p) => ({ ...p, share: false })); }
  };

  const inputCls = "w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-green-700";

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center">Ship it</h1>
      <p className="text-center text-gray-500 mt-3 mb-8 text-sm sm:text-base">Send the report by email, or grab a passcode-protected share link.</p>

      <div className="v1-card p-6 sm:p-8 space-y-5">
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Subject line</label>
            <button onClick={suggestSubjects} disabled={loading.suggest} className="text-xs font-bold flex items-center gap-1" style={{ color: "#E37400" }} data-testid="v1-suggest-subjects-button">
              {loading.suggest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Suggest with AI
            </button>
          </div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="v1-subject-input" />
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => setSubject(s.subject)}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-300 bg-white hover:border-green-700 text-left"
                  data-testid="v1-subject-suggestion">
                  <span className="font-bold text-gray-400 mr-1">{s.type}:</span>{s.subject}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Recipients</label>
          <input value={recipients} onChange={(e) => setRecipients(e.target.value)}
            placeholder="name@company.com, another@company.com"
            className={`${inputCls} mt-1.5`} data-testid="v1-recipients-input" />
          <div className="text-[11px] text-gray-400 mt-1">Separate multiple addresses with commas</div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button onClick={sendEmail} disabled={loading.send} className="v1-btn-primary flex-1 flex items-center justify-center gap-2" data-testid="v1-send-email-button">
            {loading.send ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send report
          </button>
          <button onClick={saveAndShare} disabled={loading.share} className="v1-btn-ghost flex-1 flex items-center justify-center gap-2" data-testid="v1-share-button">
            {loading.share ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Create share link
          </button>
        </div>

        {sentTo && (
          <div className="flex items-center gap-2 p-4 rounded-xl bg-[#E6F4EA] text-[#137333] text-sm font-semibold" data-testid="v1-sent-confirmation">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> Report delivered to {sentTo}
          </div>
        )}
        {shareUrl && (
          <div className="p-4 rounded-xl bg-[#E8F0FE] text-sm" data-testid="v1-share-confirmation">
            <div className="font-bold text-[#1A73E8] mb-1 flex items-center gap-2"><Link2 className="w-4 h-4" /> Share link ready — passcode: <code className="bg-white px-1.5 rounded">ecom2026</code></div>
            <div className="flex items-center gap-2 mt-2">
              <input readOnly value={shareUrl} className="flex-1 text-xs bg-white border border-blue-200 rounded-lg px-3 py-2" data-testid="v1-share-url" />
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="p-2 rounded-lg bg-white border border-blue-200 hover:bg-blue-50" data-testid="v1-copy-share-button">
                <Copy className="w-4 h-4 text-[#1A73E8]" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={() => goTo(2)} className="v1-btn-ghost" data-testid="v1-send-back-button">← Back to preview</button>
      </div>
    </div>
  );
}
